import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tab, Tabs } from "react-bootstrap";
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { onEnterOrSpace } from '../../../helpers/keyboardA11y';
import LightHamburgerIcon from '../../../assets/images/Light_Web_Hamburger_Icon.svg';
import LightProfileIcon from '../../../assets/images/Light_Web_Profile_Icon.svg';
import LightCalendarIcon from '../../../assets/images/Light_Web_Calendar_Icon.svg';
import LightNotificationIcon from '../../../assets/images/Light_Notification_Btn.svg';
import RovvrIcon from '../../../assets/images/Rovr_Icon.png';
import NoTaskInfo_Icon from '../../../assets/images/Web_NoTask_Icon.svg';
import StepModal from './StepModal';
import { TourSteps } from './tourSteps';
import TourModeTask from './TourModeTask';
import ExitTourModal from './ExitTourModal';
import LearnRovrModal from './LearnRovrModal';
import * as taskactions from '../../../modules/task/taskAndShiftAction';

/**
 * TourModeScreen component for displaying interactive tour mode
 * Optimized with React.memo, useCallback, and useMemo for better performance
 */
const TourModeScreen = React.memo((props) => {
    const [currentTourStep, setCurrentTourStep] = useState(0);
    const [isOpen, setIsOpen] = useState('open');
    const [showExitTour, setShowExitTour] = useState(false);
    const [showLearnRovr, setShowLearnRovr] = useState(null);

    // Memoize constants to prevent unnecessary recreations
    const handActionButton = useMemo(() => [3, 9, 11], []);

    // Memoize computed values
    const currentStep = useMemo(() => TourSteps[currentTourStep], [currentTourStep]);
    
    const shouldShowNoTaskInfo = useMemo(() => {
        return currentTourStep < 2 || currentTourStep === 14;
    }, [currentTourStep]);

    const shouldShowTourModeTask = useMemo(() => {
        return currentTourStep >= 2 && currentTourStep < 14;
    }, [currentTourStep]);

    // Memoize user display information
    const userDisplayInfo = useMemo(() => ({
        fullName: `${props.decodedToken?.SM_USER_FIRSTNAME || ''} ${props.decodedToken?.SM_USER_LASTNAME || ''}`.trim(),
        station: props.decodedToken?.SM_STATION || 'Unknown'
    }), [props.decodedToken?.SM_USER_FIRSTNAME, props.decodedToken?.SM_USER_LASTNAME, props.decodedToken?.SM_STATION]);

    // Memoize container styles to prevent object recreation
    const containerStyles = useMemo(() => ({
        display: "flex", 
        justifyContent: 'space-between'
    }), []);

    const logoStyles = useMemo(() => ({
        width: '50px', 
        height: '50px'
    }), []);

    const noTaskStyles = useMemo(() => ({
        marginLeft: "4.5rem"
    }), []);

    // Optimize callback functions with useCallback
    const nextTourStep = useCallback(() => {
        const current = currentTourStep;
        if (current === 2 && props.displayRovrTour && props.actions?.completeTourMode) {
            props.actions.completeTourMode({
                userId: props.userId,
                stationCode: props.decodedToken?.SM_STATION,
                companyCode: props.decodedToken?.amrcompany
            });
        }   
        const next = current + 1;
        setCurrentTourStep(next);
    }, [currentTourStep, props.displayRovrTour, props.actions, props.userId, props.decodedToken?.SM_STATION, props.decodedToken?.amrcompany]);

    const previousTourStep = useCallback(() => {
        const current = currentTourStep;
        const prev = TourSteps[current]?.previousStep;
        if (typeof prev === 'number') {
            setCurrentTourStep(prev);
        }
    }, [currentTourStep]);

    const handleExitTourToggle = useCallback((state) => {
        setShowExitTour(state);
    }, []);

    const handleLearnRovrToggle = useCallback((state) => {
        setShowLearnRovr(state);
    }, []);

    const handleIsOpenToggle = useCallback((state) => {
        setIsOpen(state);
    }, []);

    const handleExitTourClick = useCallback(() => {
        setShowExitTour(true);
    }, []);

    return(
        <div className='tourContainer' >  
            {showExitTour && (
                <ExitTourModal
                    showExitTour={showExitTour}
                    setShowExitTour={handleExitTourToggle}
                    currentTourStep={currentTourStep}
                />
            )}
            
            {currentStep?.modal && !showExitTour && showLearnRovr === null && (
                <StepModal
                    showModal={currentStep.modal}
                    setCurrentTourStep={setCurrentTourStep}
                    previousTourStep={previousTourStep}
                    nextTourStep={nextTourStep}
                    currentTourStep={currentTourStep}
                    setShowExitTour={handleExitTourToggle}
                    setShowLearnRovr={handleLearnRovrToggle}
                />
            )}
            
            {showLearnRovr !== null && (
                <LearnRovrModal
                    show={showLearnRovr}
                    setShowLearnRovr={handleLearnRovrToggle}
                />
            )}
            
            <div className='tourTitle'>
                <p>
                    Rovr+ Tour Mode
                </p>
                <span role="button" tabIndex={0} onClick={handleExitTourClick} onKeyDown={onEnterOrSpace(handleExitTourClick)}>X</span>
            </div>
            
            <div className="navContainer">
                <div className="container" style={containerStyles}>
                    <div className="navbar-brand" >
                        <img 
                            style={logoStyles} 
                            src={RovvrIcon}
                            alt="Rovr Icon"
                        />
                    </div>
                    <div className="navbarTourIcons" >
                        <img src={LightNotificationIcon} alt="Bell Icon" className="bellIconTour"/>
                        <img src={LightCalendarIcon} className="calendarIconTour" alt="Calendar icon"/>
                        <div className="headerVerticalLine" />
                        <div className="headerProfileIconTour">
                            <img src={LightProfileIcon} alt="Profile icon"/>
                        </div>
                        <div className="headerProfileTextTour">{userDisplayInfo.fullName}</div> 
                        <div className="headerVerticalLine" />
                        <div className="headerProfileText stationCode"> 
                            Station: {userDisplayInfo.station}
                        </div>
                        <img className="calendarIconTour" src={LightHamburgerIcon} alt="Hamburger Menu"/>
                    </div>
                </div>
            </div>

        {/* My task and search tabs */}
        <div className="container pb-9 tabContainer">
            <Tabs 
                fill 
                id="controlled-tab-example" 
                activeKey={"home"}
            >
                <Tab eventKey="home" title="My Task" />
                <Tab eventKey="profile" title="Search" className="inactive-task-tab" />
            </Tabs>
                   
            {shouldShowNoTaskInfo && (
                <p className="no-task-info" style={noTaskStyles}>
                    <img
                        src={NoTaskInfo_Icon}
                        alt="No task information icon"
                    />
                    No task assigned at this moment. Please stand by for future assignment.
                </p> 
            )}

            {shouldShowTourModeTask && (
                <TourModeTask
                    currentTourStep={currentTourStep}
                    handActionButton={handActionButton}
                    isOpen={isOpen}
                    setIsOpen={handleIsOpenToggle}
                    nextTourStep={nextTourStep}
                    previousTourStep={previousTourStep}
                />
            )}
        </div>
    </div>
    );
});

// Display name for debugging
TourModeScreen.displayName = 'TourModeScreen';

// PropTypes for type safety and documentation
TourModeScreen.propTypes = {
    /** User ID from authentication */
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Decoded JWT token containing user information */
    decodedToken: PropTypes.shape({
        SM_USER_FIRSTNAME: PropTypes.string,
        SM_USER_LASTNAME: PropTypes.string,
        SM_STATION: PropTypes.string,
        amrcompany: PropTypes.string
    }),
    /** Whether to display the Rovr tour */
    displayRovrTour: PropTypes.bool,
    /** Redux actions for tour mode */
    actions: PropTypes.shape({
        completeTourMode: PropTypes.func
    })
};

// Memoize mapStateToProps to prevent unnecessary re-computations
const mapDispatchToState = (state) => ({
    userId: state.AuthenticationReducer?.userId,
    decodedToken: state.AuthenticationReducer?.decodedToken,
    displayRovrTour: state.taskAndShiftReducer?.displayRovrTour
});

// Memoize mapDispatchToProps to prevent unnecessary re-computations  
const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ ...taskactions }, dispatch)
});

export default connect(
    mapDispatchToState,
    mapDispatchToProps
)(TourModeScreen);
